import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useClient } from 'urql';
import { useUi } from '@/stores/ui.store';
import { useWorkspace } from '@/stores/workspace.store';
import { Q_CONTENT_ITEMS, M_SET_CONTENT_TRIAGE, M_CREATE_CONTENT_ITEM, Q_URL_METADATA, Q_SOURCES } from '@/lib/queries';
import { Modal } from '@/components/wireframe/ui';

interface ContentItem {
  id: string;
  sourceId: string | null;
  sourceLabel: string | null;
  sourceKind: string | null;
  title: string;
  url: string | null;
  summary: string | null;
  used: boolean;
  postCount: number;
  relevance: number | null;
  triageStatus: 'pending' | 'kept' | 'dismissed';
  publishedAt: string | null;
  createdAt: string | null;
}

interface SourceRow {
  id: string;
  label: string;
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function relevanceColor(r: number | null): string {
  if (r == null) return 'bg-stone-400';
  if (r >= 80) return 'bg-emerald-500';
  if (r >= 60) return 'bg-amber-500';
  return 'bg-stone-400';
}

function statusPill(item: ContentItem): { label: string; className: string } {
  if (item.used) return { label: 'used', className: 'bg-emerald-100 text-emerald-700' };
  if (item.triageStatus === 'kept') return { label: 'saved', className: 'bg-stone-100 text-stone-600' };
  return { label: 'new', className: 'bg-violet-100 text-violet-700' };
}

export default function ContentRoute() {
  const nav = useNavigate();
  const toast = useUi((s) => s.toast);
  const [searchParams, setSearchParams] = useSearchParams();

  const projectId = useWorkspace((s) => s.project_id);

  const sourceParam = searchParams.get('source') ?? 'all';
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [preview, setPreview] = useState<ContentItem | null>(null);

  // Manual "Add content" modal. Auto-opens when navigated to with ?add=1 (e.g.
  // from the dashboard quick-action), then strips the param.
  const [showAdd, setShowAdd] = useState(searchParams.get('add') === '1');
  const [saving, setSaving] = useState(false);
  const [fetchingMeta, setFetchingMeta] = useState(false);
  const [form, setForm] = useState<{ title: string; url: string; summary: string }>({ title: '', url: '', summary: '' });
  const client = useClient();

  const [itemsQ, refetch] = useQuery({
    query: Q_CONTENT_ITEMS,
    variables: {
      sourceId: sourceParam === 'all' ? undefined : sourceParam,
      search: search.trim() || undefined,
      orderBy: 'relevance',
      limit: 200,
    },
  });
  const [sourcesQ] = useQuery({ query: Q_SOURCES });
  const [, setTriage] = useMutation(M_SET_CONTENT_TRIAGE);
  const [, createContentItem] = useMutation(M_CREATE_CONTENT_ITEM);

  const sources: SourceRow[] = useMemo(
    () => (sourcesQ.data?.contentSources ?? []).map((s: any): SourceRow => ({ id: String(s.id), label: s.label })),
    [sourcesQ.data],
  );

  const allItems: ContentItem[] = useMemo(
    () =>
      (itemsQ.data?.contentItems ?? []).map((i: any): ContentItem => ({
        id: String(i.id),
        sourceId: i.sourceId != null ? String(i.sourceId) : null,
        sourceLabel: i.sourceLabel ?? null,
        sourceKind: i.sourceKind ?? null,
        title: i.title ?? '',
        url: i.url ?? null,
        summary: i.summary ?? null,
        used: Boolean(i.used),
        postCount: i.postCount ?? 0,
        relevance: i.relevance ?? null,
        triageStatus: (i.triageStatus ?? 'pending') as ContentItem['triageStatus'],
        publishedAt: i.publishedAt ?? null,
        createdAt: i.createdAt ?? null,
      })),
    [itemsQ.data],
  );

  // Lifecycle: scraped items land in the Review queue (pending). Keeping one
  // promotes it to the library (kept); dismissing hides it. So the library shows
  // ONLY kept items — pending lives in /app/review, not here.
  const library = allItems.filter((i) => i.triageStatus === 'kept');
  const pendingReview = allItems.filter((i) => i.triageStatus === 'pending').length;

  const kinds = useMemo(
    () => [...new Set(library.map((i) => i.sourceKind).filter((k): k is string => !!k))],
    [library],
  );

  const visible = library.filter((i) => kindFilter === 'all' || i.sourceKind === kindFilter);

  const setSourceFilter = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') next.delete('source');
    else next.set('source', id);
    setSearchParams(next);
  };

  const openInComposer = (item: ContentItem) => {
    nav('/app/create', {
      state: { prefill: { contentItemId: item.id, title: item.title, summary: item.summary, url: item.url } },
    });
  };

  const closeAdd = () => {
    setShowAdd(false);
    setForm({ title: '', url: '', summary: '' }); // reset so reopening starts blank
    setFetchingMeta(false);
    if (searchParams.get('add')) { const n = new URLSearchParams(searchParams); n.delete('add'); setSearchParams(n, { replace: true }); }
  };

  const urlOk = !form.url.trim() || /^https?:\/\/.+/i.test(form.url.trim());
  const canAdd = !!projectId && form.title.trim().length > 0 && urlOk && !saving && !fetchingMeta;

  // Pull the page's title/description so the user doesn't have to type them.
  // Triggered on URL blur and via the explicit "Fetch title" button.
  const fetchMeta = async () => {
    const url = form.url.trim();
    if (!url || !/^https?:\/\/.+/i.test(url) || fetchingMeta) return;
    setFetchingMeta(true);
    const r = await client.query(Q_URL_METADATA, { url }, { requestPolicy: 'network-only' }).toPromise();
    setFetchingMeta(false);
    if (r.error || !r.data?.urlMetadata) { toast("Couldn't fetch the page — type the title manually", 'warning'); return; }
    const m = r.data.urlMetadata;
    if (!m.title && !m.summary) { toast('No title found on that page — type it manually', 'warning'); return; }
    setForm((f) => ({
      ...f,
      title: f.title.trim() ? f.title : (m.title ?? f.title),
      summary: f.summary.trim() ? f.summary : (m.summary ?? f.summary),
    }));
    toast('Title fetched', 'success');
  };

  const submitAdd = async () => {
    if (!canAdd) return;
    setSaving(true);
    const r = await createContentItem({
      input: { projectId, title: form.title.trim(), url: form.url.trim() || undefined, summary: form.summary.trim() || undefined },
    });
    setSaving(false);
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast('Added to library');
    setForm({ title: '', url: '', summary: '' });
    closeAdd();
    refetch({ requestPolicy: 'network-only' });
  };

  const dismiss = async (item: ContentItem) => {
    const r = await setTriage({ id: item.id, status: 'dismissed' });
    if (r.error) {
      toast(r.error.message, 'error');
      return;
    }
    toast('Dismissed');
    refetch({ requestPolicy: 'network-only' });
  };

  const loading = itemsQ.fetching && !itemsQ.data;

  return (
    <div className="p-8 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-stone-900">Content library</h1>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium whitespace-nowrap"
        >
          + Add content
        </button>
      </div>

      {pendingReview > 0 && (
        <button
          onClick={() => nav('/app/review')}
          className="w-full bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4 flex items-center justify-between hover:border-violet-300 transition-colors text-left"
        >
          <p className="font-semibold text-stone-900 flex items-center gap-2">
            <span className="text-violet-600">◧</span> {pendingReview} new {pendingReview === 1 ? 'item is' : 'items are'} waiting in your Review queue
          </p>
          <span className="text-sm text-violet-600 font-medium">Triage now →</span>
        </button>
      )}

      <div className="flex gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="text"
            placeholder="Search content..."
            className="w-full pl-10 pr-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">⌕</span>
        </div>
        <select
          value={sourceParam}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="px-4 py-2.5 border border-stone-200 rounded-lg text-stone-600 focus:outline-none focus:ring-2 focus:ring-violet-500"
        >
          <option value="all">All sources</option>
          {sources.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        {kinds.length > 0 && (
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="px-4 py-2.5 border border-stone-200 rounded-lg text-stone-600 focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="all">All kinds</option>
            {kinds.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <div className="bg-white border border-stone-200 rounded-xl p-12 text-center text-stone-400">
          <p className="font-medium text-stone-500">Loading content…</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-300 rounded-xl p-12 text-center text-stone-400">
          <p className="text-3xl mb-2">◰</p>
          <p className="font-medium text-stone-600">Nothing saved here yet</p>
          <p className="text-sm">
            {pendingReview > 0
              ? `Items you keep from the Review queue land here. ${pendingReview} ${pendingReview === 1 ? 'item is' : 'items are'} waiting to triage.`
              : 'Add a source to start pulling content, then keep the items worth posting.'}
          </p>
          <button onClick={() => nav(pendingReview > 0 ? '/app/review' : '/app/sources')} className="mt-3 text-sm text-violet-600 font-medium">
            {pendingReview > 0 ? 'Go to Review queue →' : 'Go to Sources →'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((item) => {
            const pill = statusPill(item);
            return (
              <div key={item.id} className="bg-white border border-stone-200 rounded-xl p-4 flex gap-4 hover:border-stone-300 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {item.sourceKind && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">{item.sourceKind}</span>
                    )}
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${pill.className}`}>{pill.label}</span>
                  </div>
                  <p className="font-semibold text-stone-900 truncate">{item.title}</p>
                  {item.summary && <p className="text-sm text-stone-500 line-clamp-2 mt-0.5">{item.summary}</p>}
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className="text-xs text-stone-400">{item.sourceLabel ?? 'Unknown source'} · {relativeTime(item.publishedAt ?? item.createdAt)}</span>
                    {item.postCount > 0 && <span className="text-xs text-stone-400">· {item.postCount} posts created</span>}
                    <span className="flex items-center gap-1.5">
                      <span className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <span className={`block h-full rounded-full ${relevanceColor(item.relevance)}`} style={{ width: `${item.relevance ?? 0}%` }} />
                      </span>
                      <span className="text-[11px] text-stone-500 font-medium">{item.relevance ?? '—'}% relevant</span>
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button onClick={() => openInComposer(item)} className="px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-xs font-medium whitespace-nowrap">Create posts</button>
                  <button onClick={() => setPreview(item)} className="px-3 py-1.5 border border-stone-200 text-stone-600 rounded-lg hover:bg-stone-50 text-xs font-medium">Preview</button>
                  <button onClick={() => dismiss(item)} className="px-3 py-1.5 text-stone-400 hover:text-red-500 text-xs">Dismiss</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {preview && (
        <Modal title={preview.title} onClose={() => setPreview(null)}>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="text-stone-500">{preview.sourceLabel ?? 'Unknown source'}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusPill(preview).className}`}>{statusPill(preview).label}</span>
            </div>
            {preview.summary && <p className="text-sm text-stone-600 leading-relaxed">{preview.summary}</p>}
            <div className="flex items-center gap-1.5 pt-2">
              <span className="w-24 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                <span className={`block h-full rounded-full ${relevanceColor(preview.relevance)}`} style={{ width: `${preview.relevance ?? 0}%` }} />
              </span>
              <span className="text-xs text-stone-500 font-medium">{preview.relevance ?? '—'}% relevant</span>
            </div>
            {preview.url && (
              <a href={preview.url} target="_blank" rel="noreferrer" className="inline-block text-sm text-violet-600 font-medium hover:text-violet-700">
                Open source ↗
              </a>
            )}
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="Add content" onClose={closeAdd}>
          <div className="space-y-4">
            <p className="text-sm text-stone-500">Paste a link and we'll pull in its title automatically — or just jot down an idea. It lands in the library straight away (skips the review queue).</p>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">URL <span className="text-stone-400 font-normal">(optional)</span></label>
              <div className="flex gap-2">
                <input
                  value={form.url}
                  onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  onBlur={fetchMeta}
                  type="url"
                  placeholder="https://example.com/article"
                  autoFocus
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${urlOk ? 'border-stone-200' : 'border-red-300'}`}
                />
                <button
                  type="button"
                  onClick={fetchMeta}
                  disabled={!form.url.trim() || !urlOk || fetchingMeta}
                  className="px-3 py-2 border border-stone-200 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-50 whitespace-nowrap"
                >
                  {fetchingMeta ? 'Fetching…' : 'Fetch title'}
                </button>
              </div>
              {!urlOk && <p className="text-xs text-red-500 mt-1">Enter a valid http(s) URL or leave it blank.</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Title <span className="text-red-500">*</span></label>
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                type="text"
                placeholder={fetchingMeta ? 'Fetching title from URL…' : 'e.g. Great article on AI marketing'}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Notes / summary <span className="text-stone-400 font-normal">(optional)</span></label>
              <textarea
                value={form.summary}
                onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                rows={3}
                placeholder="Why this is worth posting about…"
                className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={closeAdd} className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-900">Cancel</button>
              <button
                onClick={submitAdd}
                disabled={!canAdd}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Adding…' : 'Add to library'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
